import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AvailableModel } from "@passio/api-client";
import type { CatalogModel, ModelCatalog, ModelTier, ModelTierInfo } from "@passio/easyai-client";
import { easyai, passio } from "./clients";

/** Used when the EasyAI catalog can't be loaded; its `tiers` carry the descriptions. */
export const DEFAULT_TIERS: ModelTierInfo[] = [
  { id: "frontier", label: "Frontier", summary: "" },
  { id: "pro", label: "Pro", summary: "" },
  { id: "fast", label: "Fast", summary: "" },
  { id: "lite", label: "Lite", summary: "" },
];
const TIER_ORDER = DEFAULT_TIERS.map((t) => t.id);
const TIER_DEFAULTS: Record<string, [intelligence: number, speed: number]> = { frontier: [5, 2], pro: [4, 3], fast: [3, 4], lite: [2, 5] };

export const TIER_COLORS: Record<string, string> = { frontier: "violet", pro: "indigo", fast: "lime", lite: "teal" };

/** A model usable with `?model=` on the Passio API, with its catalog tier and ratings. */
export interface ModelInfo {
  tag: string;
  name: string;
  provider: string;
  description?: string;
  /** Billed tokens = model tokens × this (text); images use `imageMultiplier`. */
  tokenMultiplier: number;
  imageMultiplier?: number;
  multimodal: boolean;
  zdr: boolean;
  /** False for preview models. */
  reliable: boolean;
  visionOnly: boolean;
  variantOf?: string;
  tier: ModelTier;
  /** 1-5. */
  intelligence: number;
  speed: number;
  /** 1 (cheapest) to 5, from the token multiplier: what a call costs you. */
  cost: number;
  /** No catalog rating yet: tier, intelligence and speed are estimated from price. */
  estimated: boolean;
  listPrice: CatalogModel["price"];
  contextWindow?: number;
  /** Comparable models in the same tier (other providers first), all usable on the API. */
  similarTo: string[];
}

/** What each `cost` level (1-5) means to someone choosing a model. Most routes default to a level-1 model. */
export const COST_LEVELS: Record<number, { label: string; summary: string }> = {
  1: { label: "Lowest", summary: "Same cost level as most route defaults. Built for high volume." },
  2: { label: "Low", summary: "A step above the route defaults, still fine for everyday volume." },
  3: { label: "Moderate", summary: "Mid-range. Worth it where users scrutinize results." },
  4: { label: "High", summary: "Expensive. For hard input where a wrong answer costs more than the call." },
  5: { label: "Premium", summary: "The most expensive. Keep for low-volume, high-stakes calls." },
};

export function costFromMultiplier(m: number): number {
  if (m <= 2) return 1;
  if (m <= 8) return 2;
  if (m <= 20) return 3;
  if (m <= 60) return 4;
  return 5;
}

export function tierFromMultiplier(m: number): ModelTier {
  if (m <= 2) return "lite";
  if (m <= 10) return "fast";
  if (m <= 40) return "pro";
  return "frontier";
}

const tierIndex = (t: string) => (TIER_ORDER.includes(t) ? TIER_ORDER.indexOf(t) : TIER_ORDER.length);
const family = (m: ModelInfo) => m.variantOf || m.tag;
const distance = (a: ModelInfo, b: ModelInfo) => Math.abs(a.intelligence - b.intelligence) + Math.abs(a.speed - b.speed) + Math.abs(a.cost - b.cost);

/** Join the API's model list (what you can call, and what it costs) with the EasyAI catalog (tier, ratings, list price). */
export function joinModels(available: AvailableModel[], catalog?: ModelCatalog): ModelInfo[] {
  const byTag = new Map((catalog?.models ?? []).map((m) => [m.api_name, m]));
  const models: ModelInfo[] = available.map((a) => {
    const c = byTag.get(a.tag);
    const tier = c?.tier ?? tierFromMultiplier(a.token_multiplier);
    const [intelligence, speed] = c ? [c.ratings.intelligence, c.ratings.speed] : (TIER_DEFAULTS[tier] ?? [3, 3]);
    return {
      tag: a.tag,
      name: a.name,
      provider: a.provider || c?.provider || "Other",
      description: a.description || c?.description || undefined,
      tokenMultiplier: a.token_multiplier,
      imageMultiplier: a.image_multiplier,
      multimodal: a.supports_multimodal,
      zdr: a.zero_data_retention,
      reliable: a.reliable,
      visionOnly: !!a.vision_only,
      variantOf: a.variant_of || undefined,
      tier,
      intelligence,
      speed,
      cost: costFromMultiplier(a.token_multiplier),
      estimated: !c || c.estimated,
      listPrice: c?.price ?? null,
      contextWindow: c?.context_window ?? undefined,
      similarTo: c?.similar_to ?? [],
    };
  });
  const tags = new Set(models.map((m) => m.tag));
  const reliable = models.filter((m) => m.reliable && !m.visionOnly);
  for (const m of models) {
    const fromCatalog = m.similarTo.filter((t) => t !== m.tag && tags.has(t));
    const sameTier = reliable
      .filter((p) => p.tier === m.tier && family(p) !== family(m) && !fromCatalog.includes(p.tag))
      .sort((a, b) => Number(a.provider === m.provider) - Number(b.provider === m.provider) || distance(m, a) - distance(m, b) || a.name.localeCompare(b.name));
    m.similarTo = [...fromCatalog, ...sameTier.map((p) => p.tag)].slice(0, 4);
  }
  return models.sort((a, b) => tierIndex(a.tier) - tierIndex(b.tier) || b.intelligence - a.intelligence || a.cost - b.cost || a.name.localeCompare(b.name));
}

/** `GET /napi/models/available`: the tags this account can send as `?model=`. */
export function useModelCatalog() {
  return useQuery({ queryKey: ["models"], queryFn: async () => (await passio.models({}, { quiet: true })).data, staleTime: 10 * 60_000 });
}

/** API models joined with the EasyAI catalog. Works without the catalog (ratings are then estimated). */
export function useModelInfo() {
  const available = useModelCatalog();
  const catalog = useQuery({ queryKey: ["model-catalog"], queryFn: () => easyai.modelCatalog({ includeDeprecated: true }), staleTime: 10 * 60_000, retry: 1 });
  const models = useMemo(() => joinModels(available.data ?? [], catalog.data), [available.data, catalog.data]);
  const byTag = useMemo(() => new Map(models.map((m) => [m.tag, m])), [models]);
  const tiers = catalog.data?.tiers?.length ? catalog.data.tiers : DEFAULT_TIERS;
  return { models, byTag, tiers, isPending: available.isPending, error: available.error as Error | null, catalogError: catalog.error as Error | null };
}
